import React from 'react';
import type { WebsitePage } from '@troia-v3/types';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { ImageUploaderField } from './widgets/ImageUploaderField';

interface PageSeoEditorProps {
  page: WebsitePage;
  onChange: (page: WebsitePage) => void;
}

/**
 * Editor do SEO por página (WebsitePage.seoConfig).
 * Campos vazios caem no padrão global (globalConfig.seoTitle/seoDescription) —
 * o render usa o seoConfig da página primeiro, com fallback pro global.
 */
export const PageSeoEditor: React.FC<PageSeoEditorProps> = ({ page, onChange }) => {
  const seo = page.seoConfig || {};

  const updateSeo = (field: string, value: unknown) => {
    onChange({ ...page, seoConfig: { ...seo, [field]: value } });
  };

  return (
    <FormSection
      icon="search"
      title="SEO da página"
      description="Título, descrição e imagem usados por buscadores e redes sociais nesta página"
      collapsible
      defaultCollapsed
    >
      <div className="space-y-4">
        <FormField
          label="Título"
          description="Aparece na aba do navegador e no Google. Vazio = título padrão do site"
        >
          <Input
            value={seo.title || ''}
            onChange={(e) => updateSeo('title', e.target.value)}
            placeholder="Título da página"
            data-testid="page-seo-title-input"
          />
        </FormField>

        <FormField label="Descrição" description="Resumo exibido nos resultados de busca (até ~160 caracteres)">
          <Textarea
            value={seo.description || ''}
            onChange={(e) => updateSeo('description', e.target.value)}
            rows={3}
            placeholder="Descrição da página"
            data-testid="page-seo-description-input"
          />
        </FormField>

        <FormField label="Palavras-chave" description="Separadas por vírgula">
          <Input
            value={(seo.keywords || []).join(', ')}
            onChange={(e) =>
              updateSeo(
                'keywords',
                e.target.value
                  .split(',')
                  .map((k) => k.trim())
                  .filter(Boolean)
              )
            }
            placeholder="imóveis, apartamentos, casas"
            data-testid="page-seo-keywords-input"
          />
        </FormField>

        <ImageUploaderField
          label="Imagem de compartilhamento (OG Image)"
          description="Imagem exibida ao compartilhar o link em redes sociais"
          value={seo.ogImage || ''}
          onChange={(url) => updateSeo('ogImage', url)}
        />
      </div>
    </FormSection>
  );
};
